import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { INDUSTRIES } from '../data/constants';
import { submitContactLead } from '../services/leadService';
import { normalizeUrl } from '../utils/validation';
import CustomSelect from '../components/common/CustomSelect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiPhone, FiMapPin, FiCheckCircle, FiAlertCircle } = FiIcons;

const SERVICE_OPTIONS = [
  'Creative & Advertising Intelligence',
  'Intelligence Subscription',
  'Campaign Strategy',
  'Creative & Content',
  'Brand Strategy & Identity',
  'Website Design & Development',
  'SEO & Digital Growth',
  'Production & Activation',
  'Other'
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    job_title: '',
    work_email: '',
    telephone: '',
    website: '',
    industry: '',
    country: '',
    service_interest: '',
    business_challenge: '',
    message: '',
    preferred_start_date: '',
    referral_source: '',
    consent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    setError(null);

    const normalizedWebsite = normalizeUrl(formData.website);
    if (formData.website && normalizedWebsite === 'invalid') {
      setError('Enter a valid website, for example maxmarkagency.com.');
      setLoading(false);
      return;
    }

    try {
      await submitContactLead(formData, normalizedWebsite);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('We couldn’t submit your enquiry. Please try again or contact info@maxmarkagency.com.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 bg-black">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-xl text-center">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-blue-600/20">
            <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 text-white">Thank you. Your enquiry has been received.</h2>
          <p className="text-xl text-zinc-400 mb-10 leading-relaxed font-light">A Maxmark strategist will contact you shortly.</p>
          <button onClick={() => setSubmitted(false)} className="text-blue-500 hover:text-blue-400 font-bold uppercase tracking-widest text-[10px] transition-colors">Send another message</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-black pt-40 pb-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 xl:gap-24">
          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <span className="inline-block py-1 px-3 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-500 text-[10px] font-bold tracking-[0.2em] uppercase mb-8">Get in Touch</span>
              <h1 className="text-5xl md:text-7xl font-serif font-bold mb-8 text-white leading-[1.1]">Let’s Talk About Your Growth.</h1>
              <p className="text-xl text-zinc-400 mb-12 font-light leading-relaxed max-w-md">Tell us what your business needs. Our team will review your enquiry and respond with the most relevant next step.</p>
              
              <div className="space-y-10 pt-10 border-t border-zinc-900">
                <div className="flex gap-6 items-start">
                  <div className="w-12 h-12 rounded-2xl bg-zinc-900 flex items-center justify-center shrink-0 border border-zinc-800"><SafeIcon icon={FiMapPin} className="w-5 h-5 text-zinc-400" /></div>
                  <div><h3 className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase mb-2">Location</h3><p className="text-lg font-medium text-white">77 Ogudu Ojota, Lagos, Nigeria</p></div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="w-12 h-12 rounded-2xl bg-zinc-900 flex items-center justify-center shrink-0 border border-zinc-800"><SafeIcon icon={FiMail} className="w-5 h-5 text-zinc-400" /></div>
                  <div><h3 className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase mb-2">Email</h3><p className="text-lg font-medium text-blue-500">info@maxmarkagency.com</p></div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-950 border border-zinc-900 p-8 md:p-12 rounded-[3rem] shadow-2xl relative overflow-hidden">
              <form onSubmit={handleSubmit} className="relative z-10 space-y-6">
                <AnimatePresence>{error && <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="p-4 bg-red-900/20 border border-red-900/30 text-red-400 text-xs rounded-xl flex items-center gap-3"><SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" />{error}</motion.div>}</AnimatePresence>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Full Name *</label><input type="text" name="full_name" className="input-field" value={formData.full_name} onChange={handleChange} required /></div>
                  <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Company Name *</label><input type="text" name="company_name" className="input-field" value={formData.company_name} onChange={handleChange} required /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Job Title *</label><input type="text" name="job_title" className="input-field" value={formData.job_title} onChange={handleChange} required /></div>
                  <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Work Email *</label><input type="email" name="work_email" className="input-field" value={formData.work_email} onChange={handleChange} required /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Telephone *</label><input type="tel" name="telephone" className="input-field" value={formData.telephone} onChange={handleChange} required /></div>
                  <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Website (Optional)</label><input type="text" name="website" className="input-field" value={formData.website} onChange={handleChange} placeholder="e.g. maxmarkagency.com" /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <CustomSelect label="Industry" options={INDUSTRIES} value={formData.industry} onChange={(val) => handleSelectChange('industry', val)} placeholder="Select Industry" required />
                  <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Country *</label><input type="text" name="country" className="input-field" value={formData.country} onChange={handleChange} required /></div>
                </div>
                <CustomSelect label="Service Required" options={SERVICE_OPTIONS} value={formData.service_interest} onChange={(val) => handleSelectChange('service_interest', val)} placeholder="Select Interest" />
                <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Primary Business Challenge *</label><textarea name="business_challenge" rows="3" className="input-field" value={formData.business_challenge} onChange={handleChange} required></textarea></div>
                <div className="space-y-2"><label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Message *</label><textarea name="message" rows="4" className="input-field" value={formData.message} onChange={handleChange} required></textarea></div>
                
                <div className="flex items-start gap-4 p-5 bg-zinc-900/50 rounded-2xl border border-zinc-800/50 group hover:border-zinc-700 transition-colors">
                  <input type="checkbox" name="consent" id="consent" className="mt-1 w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-blue-600" checked={formData.consent} onChange={handleChange} required />
                  <label htmlFor="consent" className="text-[11px] text-zinc-500 leading-relaxed cursor-pointer select-none">I agree to the processing of my data according to the <Link to="/privacy" className="text-blue-500 hover:underline">Privacy Policy</Link>.</label>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-6 rounded-2xl font-bold uppercase tracking-[0.2em] text-xs transition-all hover:bg-blue-700 disabled:opacity-50 shadow-lg shadow-blue-600/20">{loading ? 'Processing...' : 'Send Enquiry'}</button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}